import React, { useEffect, useState } from "react";
import {
  getClients,
  getNotesTasks,
  createNoteTask,
  updateNoteTask,
  deleteNoteTask,
} from "../api";

export default function NotesTasks() {
  const [clients, setClients] = useState([]);
  const [items, setItems] = useState([]);
  const [filteredItems, setFilteredItems] = useState([]);
  const [selectedClientId, setSelectedClientId] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [loadingClients, setLoadingClients] = useState(false);
  const [loadingItems, setLoadingItems] = useState(false);
  const [saving, setSaving] = useState(false);

  const [form, setForm] = useState({
    client_id: "",
    item_type: "task",
    title: "",
    description: "",
    status: "open",
    due_date: "",
  });

  async function loadClients() {
    try {
      setLoadingClients(true);
      setError("");
      const data = await getClients();
      const list = Array.isArray(data) ? data : [];
      setClients(list);

      if (list.length > 0) {
        setSelectedClientId((prev) => {
          const prevExists = list.some((client) => String(client.id) === 
String(prev));
          return prevExists ? prev : "";
        });
      } else {
        setSelectedClientId("");
      }
    } catch (err) {
      setError(err?.message || "Failed to load clients");
      setClients([]);
    } finally {
      setLoadingClients(false);
    }
  }

  async function loadItems() {
    try {
      setLoadingItems(true);
      setError("");
      const data = await getNotesTasks();
      const list = Array.isArray(data) ? data : [];
      setItems(list);
    } catch (err) {
      setError(err?.message || "Failed to load notes/tasks");
      setItems([]);
    } finally {
      setLoadingItems(false);
    }
  }

  useEffect(() => {
    loadClients();
    loadItems();
  }, []);

  useEffect(() => {
    let list = [...items];

    if (selectedClientId) {
      list = list.filter((item) => String(item.client_id) === 
String(selectedClientId));
    }

    const term = searchTerm.trim().toLowerCase();

    if (term) {
      list = list.filter((item) => {
        const clientName = (item.full_name || "").toLowerCase();
        const itemType = (item.item_type || "").toLowerCase();
        const title = (item.title || "").toLowerCase();
        const description = (item.description || "").toLowerCase();
        const status = (item.status || "").toLowerCase();

        return (
          clientName.includes(term) ||
          itemType.includes(term) ||
          title.includes(term) ||
          description.includes(term) ||
          status.includes(term)
        );
      });
    }

    setFilteredItems(list);
  }, [items, selectedClientId, searchTerm]);

  function resetForm() {
    setEditingId(null);
    setForm({
      client_id: "",
      item_type: "task",
      title: "",
      description: "",
      status: "open",
      due_date: "",
    });
  }

  async function handleSubmit(e) {
    e.preventDefault();

    try {
      setSaving(true);
      setError("");
      setSuccessMessage("");

      const payload = {
        client_id: form.client_id ? Number(form.client_id) : null,
        item_type: form.item_type,
        title: form.title,
        description: form.description,
        status: form.status,
        due_date: form.due_date,
      };

      if (editingId) {
        await updateNoteTask(editingId, payload);
        setSuccessMessage("Note/task updated successfully");
      } else {
        await createNoteTask(payload);
        setSuccessMessage("Note/task created successfully");
      }

      resetForm();
      await loadItems();
    } catch (err) {
      setError(err?.message || "Failed to save note/task");
    } finally {
      setSaving(false);
    }
  }

  function handleEdit(item) {
    setEditingId(item.id);
    setForm({
      client_id: item.client_id ? String(item.client_id) : "",
      item_type: item.item_type || "task",
      title: item.title || "",
      description: item.description || "",
      status: item.status || "open",
      due_date: item.due_date || "",
    });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }

  async function handleDelete(itemId) {
    const confirmed = window.confirm("Delete this note/task?");
    if (!confirmed) return;

    try {
      setError("");
      setSuccessMessage("");
      await deleteNoteTask(itemId);
      if (editingId === itemId) {
        resetForm();
      }
      setSuccessMessage("Note/task deleted successfully");
      await loadItems();
    } catch (err) {
      setError(err?.message || "Failed to delete note/task");
    }
  }

  function formatDate(value) {
    if (!value) return "No due date";
    try {
      const date = new Date(value);
      if (Number.isNaN(date.getTime())) return value;
      return date.toLocaleDateString();
    } catch {
      return value;
    }
  }

  return (
    <div style={{ padding: "28px", background: "#f3f6fb", minHeight: "100vh" }}>
      <div
        style={{
          marginBottom: "28px",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: "20px",
          flexWrap: "wrap",
        }}
      >
        <div>
          <div
            style={{
              fontSize: "18px",
              color: "#6b7280",
              marginBottom: "8px",
              fontWeight: "500",
            }}
          >
            Integrity Suite CRM
          </div>
          <h1
            style={{
              fontSize: "64px",
              fontWeight: "800",
              margin: 0,
              lineHeight: 1,
              color: "#0f172a",
            }}
          >
            Notes / Tasks
          </h1>
        </div>

        <div style={{ display: "flex", gap: "14px", flexWrap: "wrap" }}>
          <button onClick={loadClients} style={topButtonStyle}>
            {loadingClients ? "Refreshing Clients..." : "Refresh Clients"}
          </button>

          <button onClick={loadItems} style={topButtonStyle}>
            {loadingItems ? "Refreshing Items..." : "Refresh Items"}
          </button>
        </div>
      </div>

      {error ? (
        <div
          style={{
            border: "2px solid #ff4d4f",
            borderRadius: "18px",
            padding: "18px 20px",
            marginBottom: "24px",
            background: "white",
            color: "#111827",
            fontSize: "18px",
          }}
        >
          {error}
        </div>
      ) : null}

      {successMessage ? (
        <div
          style={{
            border: "2px solid #22c55e",
            borderRadius: "18px",
            padding: "18px 20px",
            marginBottom: "24px",
            background: "white",
            color: "#111827",
            fontSize: "18px",
          }}
        >
          {successMessage}
        </div>
      ) : null}

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1.05fr 1.4fr",
          gap: "24px",
          alignItems: "start",
        }}
      >
        <div style={panelStyle}>
          <div style={panelHeaderStyle}>
            {editingId ? "Edit Note / Task" : "Create Note / Task"}
          </div>
          <div
            style={{
              fontSize: "16px",
              color: "#64748b",
              marginBottom: "20px",
            }}
          >
            Keep follow-ups, reminders, and notes tied to clients or create general 
internal workflow items.
          </div>

          <form onSubmit={handleSubmit}>
            <label style={labelStyle}>Client</label>
            <select
              value={form.client_id}
              onChange={(e) => setForm({ ...form, client_id: e.target.value })}
              style={inputStyle}
            >
              <option value="">No client selected</option>
              {clients.map((client) => (
                <option key={client.id} value={String(client.id)}>
                  {(client.full_name || "Client " + client.id) + " (ID: " + client.id 
+ ")"}
                </option>
              ))}
            </select>

            <label style={labelStyle}>Type</label>
            <select
              value={form.item_type}
              onChange={(e) => setForm({ ...form, item_type: e.target.value })}
              style={inputStyle}
            >
              <option value="task">Task</option>
              <option value="note">Note</option>
            </select>

            <label style={labelStyle}>Title</label>
            <input
              type="text"
              value={form.title}
              onChange={(e) => setForm({ ...form, title: e.target.value })}
              placeholder="Enter title"
              style={inputStyle}
            />

            <label style={labelStyle}>Description</label>
            <textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              rows="5"
              placeholder="Enter details"
              style={textareaInputStyle}
            />

            <label style={labelStyle}>Status</label>
            <select
              value={form.status}
              onChange={(e) => setForm({ ...form, status: e.target.value })}
              style={inputStyle}
            >
              <option value="open">Open</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
            </select>

            <label style={labelStyle}>Due Date</label>
            <input
              type="date"
              value={form.due_date}
              onChange={(e) => setForm({ ...form, due_date: e.target.value })}
              style={inputStyle}
            />

            <div style={{ display: "flex", gap: "12px", marginTop: "10px" }}>
              <button type="submit" style={primaryButtonStyle}>
                {saving ? "Saving..." : editingId ? "Save Changes" : "Create Item"}
              </button>

              <button
                type="button"
                onClick={resetForm}
                style={secondaryButtonStyle}
              >
                Clear
              </button>
            </div>
          </form>
        </div>

        <div style={panelStyle}>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              gap: "16px",
              flexWrap: "wrap",
              marginBottom: "18px",
            }}
          >
            <div>
              <div style={panelHeaderStyle}>Saved Notes / Tasks</div>
              <div
                style={{
                  fontSize: "16px",
                  color: "#64748b",
                  marginTop: "4px",
                }}
              >
                Review, search, and manage all workflow notes and tasks.
              </div>
            </div>

            <div
              style={{
                minWidth: "280px",
                flex: 1,
                maxWidth: "420px",
              }}
            >
              <input
                type="text"
                placeholder="Search by client, type, title, description, or status"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={searchInputStyle}
              />
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: "18px",
              marginBottom: "20px",
            }}
          >
            <MiniStatCard
              title="Total Items"
              value={items.length}
              subtitle="All notes and tasks"
            />
            <MiniStatCard
              title="Showing"
              value={filteredItems.length}
              subtitle="Current filtered results"
            />
          </div>

          {loadingItems ? (
            <div style={emptyStateStyle}>Loading notes/tasks...</div>
          ) : filteredItems.length === 0 ? (
            <div style={emptyStateStyle}>
              {items.length === 0
                ? "No notes or tasks created yet."
                : "No notes/tasks matched your current filter."}
            </div>
          ) : (
            <div style={{ display: "grid", gap: "18px" }}>
              {filteredItems.map((item) => (
                <div key={item.id} style={itemCardStyle}>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "start",
                      gap: "16px",
                      flexWrap: "wrap",
                      marginBottom: "12px",
                    }}
                  >
                    <div>
                      <div
                        style={{
                          fontSize: "24px",
                          fontWeight: "800",
                          color: "#0f172a",
                          marginBottom: "8px",
                        }}
                      >
                        {item.title || "Untitled"}
                      </div>
                      <div style={infoLineStyle}>
                        {"Type: " + (item.item_type || "task")}
                      </div>
                      <div style={infoLineStyle}>
                        {"Client: " + (item.client_name || "General item")}
                      </div>
                      <div style={infoLineStyle}>
                        {"Status: " + (item.status || "open")}
                      </div>
                      <div style={infoLineStyle}>
                        {"Due: " + formatDate(item.due_date)}
                      </div>
                    </div>

                    <div style={{ display: "flex", gap: "10px", flexWrap: "wrap" }}>
                      <button
                        type="button"
                        onClick={() => handleEdit(item)}
                        style={editButtonStyle}
                      >
                        Edit
                      </button>

                      <button
                        type="button"
                        onClick={() => handleDelete(item.id)}
                        style={deleteButtonStyle}
                      >
                        Delete
                      </button>
                    </div>
                  </div>

                  <div
                    style={{
                      background: "#ffffff",
                      border: "1px solid #e5e7eb",
                      borderRadius: "18px",
                      padding: "18px",
                      fontSize: "16px",
                      color: "#111827",
                      lineHeight: "1.7",
                    }}
                  >
                    {item.description || "No description provided"}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function MiniStatCard({ title, value, subtitle }) {
  return (
    <div
      style={{
        background: "#f8fbff",
        borderRadius: "20px",
        padding: "20px",
        border: "1px solid #dbeafe",
      }}
    >
      <div
        style={{
          fontSize: "16px",
          fontWeight: "700",
          color: "#64748b",
          marginBottom: "10px",
        }}
      >
        {title}
      </div>
      <div
        style={{
          fontSize: "42px",
          fontWeight: "800",
          color: "#0f172a",
          lineHeight: 1,
          marginBottom: "8px",
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: "14px",
          color: "#94a3b8",
        }}
      >
        {subtitle}
      </div>
    </div>
  );
}

const panelStyle = {
  background: "white",
  borderRadius: "24px",
  padding: "24px",
  border: "1px solid #e5e7eb",
  boxShadow: "0 10px 24px rgba(15,23,42,0.06)",
};

const panelHeaderStyle = {
  fontSize: "30px",
  fontWeight: "800",
  color: "#0f172a",
  marginBottom: "8px",
};

const labelStyle = {
  display: "block",
  marginBottom: "8px",
  fontSize: "16px",
  fontWeight: "700",
  color: "#334155",
};

const inputStyle = {
  width: "100%",
  padding: "16px 18px",
  marginBottom: "16px",
  borderRadius: "16px",
  border: "1px solid #d1d5db",
  fontSize: "17px",
  boxSizing: "border-box",
  background: "#ffffff",
};

const textareaInputStyle = {
  width: "100%",
  padding: "16px 18px",
  marginBottom: "16px",
  borderRadius: "16px",
  border: "1px solid #d1d5db",
  fontSize: "17px",
  boxSizing: "border-box",
  background: "#ffffff",
  resize: "vertical",
};

const searchInputStyle = {
  width: "100%",
  padding: "16px 18px",
  borderRadius: "16px",
  border: "1px solid #d1d5db",
  fontSize: "16px",
  boxSizing: "border-box",
  background: "#ffffff",
};

const primaryButtonStyle = {
  flex: 1,
  padding: "16px",
  borderRadius: "18px",
  border: "none",
  background: "#0f172a",
  color: "white",
  fontSize: "18px",
  fontWeight: "700",
  cursor: "pointer",
};

const secondaryButtonStyle = {
  padding: "16px 22px",
  borderRadius: "18px",
  border: "1px solid #cbd5e1",
  background: "white",
  color: "#0f172a",
  fontSize: "16px",
  fontWeight: "700",
  cursor: "pointer",
};

const topButtonStyle = {
  padding: "16px 24px",
  borderRadius: "16px",
  border: "none",
  background: "#0f172a",
  color: "white",
  fontSize: "17px",
  fontWeight: "600",
  cursor: "pointer",
  boxShadow: "0 8px 20px rgba(15,23,42,0.15)",
};

 const editButtonStyle = {
  padding: "12px 18px",
  borderRadius: "14px",
  background: "#D4AF37",
  color: "#111111",
  border: "none",
  fontWeight: "700",
  cursor: "pointer",
};

const deleteButtonStyle = {
  padding: "12px 18px",
  borderRadius: "14px",
  background: "#dc2626",
  color: "white",
  border: "none",
  fontWeight: "700",
  cursor: "pointer",
};

const itemCardStyle = {
  background: "linear-gradient(180deg, #ffffff 0%, #f8fbff 100%)",
  border: "1px solid #e2e8f0",
  borderRadius: "22px",
  padding: "22px",
};

const infoLineStyle = {
  fontSize: "15px",
  color: "#64748b",
  marginBottom: "6px",
};

const emptyStateStyle = {
  padding: "28px 0",
  color: "#94a3b8",
  fontSize: "17px",
}
